// @ts-nocheck
/**
 * Edit User — same 4-step wizard as UserAddPage, minus the credentials
 * block (password reset lives on the dedicated route). Prefills every
 * field from GET /api/obd/users/profile/:id, then POSTs the same URL
 * on submit (v1 uses POST-to-profile for the update path).
 *
 * Steps:
 *   0 Account   — name, role (locked), email (+ OTP if changed)
 *   1 Access    — ACL, account type, company / call center
 *   3 Review    — summary + save
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { toDateInputString, todayInputString, minDateStr, formatExpiryForDisplay } from '@/utilities/expiryDate';
import { blockManualDateEntry, blockDatePaste } from '@/utilities/dateInputHelpers';
import {
  ArrowLeft,
  ArrowRight,
  Building,
  Check,
  ClipboardCheck,
  Loader2,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Stepper, useStepper } from '@/components/ui/stepper';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils/cn';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { AvailabilityHint, useUserEmailAvailability } from '@/hooks/useAvailability';
import { validateEmail } from '@/utilities/userCreation';
import { getUser } from '@/utilities/setting';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import { ROUTES, USER_ROLES, USERS_ROLE, MODULE_ACL } from '@/utilities/constant';
import { parseAgentLeadsInput, currentUserHasCustomAgentLeads } from '@/utilities/agentLeadsQuota';
import { useAuth } from '@/views/dashboard/auth/auth-utils';
import {
  updateUserSchema,
  isAclRequired,
  isCallCentreRequired,
  isCompanyRequired,
  type UpdateUserInput,
} from './schema';
import { useUpdateUser, useUserProfile } from './api';
import {
  useAclSettings,
  useCallCentreList,
  useCompanyList,
} from './lookups/api';
import { EmailVerifier } from './components/EmailVerifier';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const STEPS = [
  { id: 'account', label: 'Account', description: 'Name, role, email', icon: UserRound },
  { id: 'access', label: 'Access', description: 'ACL & assignment', icon: Building },
  { id: 'review', label: 'Review', description: 'Confirm & save', icon: ClipboardCheck },
] as const;

/** Per-step fields for validation. Email is grouped in Step 0. */
const EDIT_STEP_FIELDS: Record<number, Array<keyof UpdateUserInput>> = {
  0: ['name', 'role', 'email', 'expiryDate'],
  1: ['aclSettings', 'accountType', 'company', 'callCentreId', 'agentLeads'],
  2: [],
};

function agentLeadsBelowAssignedMessage(value: unknown, assigned: number) {
  if (!assigned) return '';
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const parsed = parseAgentLeadsInput(raw);
  if (!parsed.ok || parsed.value >= assigned) return '';
  return `Cannot be below ${assigned}. That many leads are already assigned.`;
}

export default function UserEditPage() {
  const navigate = useNavigate();
  useAuth();
  const { canEdit } = useModuleAcl(MODULE_ACL.USERS);
  const { id: editUserId } = useParams();
  const stepper = useStepper(STEPS.length);

  const [originalEmail, setOriginalEmail] = useState<string>('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerifiedToken, setEmailVerifiedToken] = useState<string>('');
  const [extendChildrenExpiry, setExtendChildrenExpiry] = useState(false);
  const [extendExpiredChildren, setExtendExpiredChildren] = useState(false);

  const creator = useMemo(() => getUser() || {}, []);
  const canConfigureLeads = useMemo(() => currentUserHasCustomAgentLeads(creator), [creator]);
  const creatorRole = creator?.role || '';
  const creatorId = creator?.id ?? null;

  const isSuperAdmin =
    creatorRole === USER_ROLES.SUPERADMIN || creatorRole === USER_ROLES.SUB_SUPERADMIN;
  const canEditAccountType = creatorRole === USER_ROLES.SUPERADMIN;
  const isCallCentreAdmin = creatorRole === USER_ROLES.CALL_CENTRE_ADMIN;
  const isCompanyAdmin = creatorRole === USER_ROLES.COMPANY_ADMIN;
  const isOwnCompany = Boolean(
    creator?.assignedCompany?.is_own_company || creator?.companies?.is_own_company,
  );

  /* ── Data hooks ─────────────────────────────────────────────── */
  const profile = useUserProfile(editUserId ?? null);
  const target = profile.data ?? null;

  const targetRole = target?.role ?? '';
  const aclList = useAclSettings(isAclRequired(targetRole) ? targetRole : null);
  const companyList = useCompanyList(!(isCompanyAdmin && isOwnCompany));
  const callCentreList = useCallCentreList(!isCallCentreAdmin);
  const updateUser = useUpdateUser(editUserId ?? null);

  const emailVerificationRequired = useMemo(
    () => creatorRequiresEmailVerificationOnUserForms(creator, { isSuperAdmin, userRole: targetRole }),
    [creator, isSuperAdmin, targetRole],
  );

  /* ── Form ───────────────────────────────────────────────────── */
  const form = useForm<UpdateUserInput>({
    resolver: zodResolver(updateUserSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      role: '',
      email: '',
      expiryDate: '',
      aclSettings: '',
      accountType: '0',
      company: '',
      callCentreId: '',
    },
  });

  /* Prefill once profile resolves. Field-name mapping matches v1's
     user-edit.js:385-437 exactly:
       • ACL → `acl_settings_id` (scalar). `acl_settings` is the whole
         nested object — using it here would send "[object Object]" and
         the backend PATCH would 400 with a bigint parse error.
       • Company → `company.id` OR `assigned_company` (a scalar) as a
         fallback. `company` itself is the joined row.
       • Call centre → `callcenter.id`. `callcenter` is the joined row. */
  useEffect(() => {
    if (!target) return;
    setOriginalEmail(String(target.email ?? ''));

    const aclId =
      target.acl_settings_id != null
        ? String(target.acl_settings_id)
        : typeof target.acl_settings === 'object' && target.acl_settings !== null
          ? String(target.acl_settings.id ?? '')
          : target.acl_settings != null
            ? String(target.acl_settings)
            : '';

    const companyId =
      target.company && typeof target.company === 'object' && target.company.id != null
        ? String(target.company.id)
        : target.assigned_company != null
          ? String(target.assigned_company)
          : '';

    const callCentreId =
      target.callcenter && typeof target.callcenter === 'object' && target.callcenter.id != null
        ? String(target.callcenter.id)
        : target.callcenterId != null
          ? String(target.callcenterId)
          : '';

    form.reset({
      name: target.name ?? '',
      role: target.role ?? '',
      email: target.email ?? '',
      // Local calendar day: an IST-midnight expiry slices to the previous UTC day.
      expiryDate: toDateInputString(target.expiry_date),
      aclSettings: aclId,
      accountType: String(target.account_type ?? '0'),
      company: companyId,
      callCentreId,
      ...(target.role === USER_ROLES.CALL_CENTRE_ADMIN && target.agent_leads != null
        ? { agentLeads: String(target.agent_leads) }
        : {}),
    });
  }, [target, form]);

  const role = form.watch('role');
  const email = form.watch('email');

  const emailChanged = email && email.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
  const emailStatus = useUserEmailAvailability(email ?? '', {
    enabled: Boolean(originalEmail) && !validateEmail(email ?? ''),
    originalValue: originalEmail,
    excludeId: target?.id ?? editUserId ?? null,
  });
  const emailReverifyRequired = emailVerificationRequired && emailChanged;

  /* Auto-select default ACL when none set + list resolves. */
  useEffect(() => {
    if (!aclList.data?.length) return;
    if (form.getValues('aclSettings')) return;
    const def = aclList.data.find((a) => a.default_for_role === role);
    if (def) form.setValue('aclSettings', String(def.id), { shouldDirty: false });
  }, [aclList.data, role, form]);

  const filteredCompanies = useMemo(() => {
    if (!companyList.data) return [];
    if (isSuperAdmin) return companyList.data.filter((c) => c.created_by == creatorId);
    return companyList.data;
  }, [companyList.data, isSuperAdmin, creatorId]);

  /* Expiry is only editable when the logged-in user is the DIRECT parent. */
  const createdBy = target?.created_by;
  const targetParentId =
    (createdBy && typeof createdBy === 'object' ? createdBy.id : createdBy) ??
    target?.createdByUser?.id ??
    target?.parent ??
    null;
  const canEditExpiry =
    creatorId != null && targetParentId != null && String(creatorId) === String(targetParentId);

  const selectedCompanyId = form.watch('company');
  const selectedCompanyExpiry = useMemo(() => {
    const comp = (companyList.data ?? []).find((c) => String(c.id) === String(selectedCompanyId));
    return comp?.expiry_date || null;
  }, [companyList.data, selectedCompanyId]);

  const companyExpiryStr = toDateInputString(selectedCompanyExpiry);
  const parentExpiryStr = minDateStr(
    creator?.expiry_date,
    creator?.assignedCompany?.expiry_date,
    creator?.assigned_company_details?.expiry_date,
  );
  const effectiveExpiryMax = minDateStr(companyExpiryStr, parentExpiryStr);
  const todayStr = todayInputString();
  const expiryCapSource =
    effectiveExpiryMax && effectiveExpiryMax === companyExpiryStr
      ? "the assigned company"
      : 'your own account';
  const expiryValue = form.watch('expiryDate');
  const expiryWithinCap =
    !canEditExpiry || !effectiveExpiryMax || !expiryValue || expiryValue <= effectiveExpiryMax;
  // Offer "extend everyone under this user" unless the date moves earlier (that
  // already clamps children) and the user can have children (agents have none).
  // Same date is allowed so children can be brought up to the current expiry.
  const originalExpiry = toDateInputString(target?.expiry_date);
  const canExtendChildren =
    canEditExpiry &&
    targetRole !== USER_ROLES.CALL_CENTRE_AGENT &&
    Boolean(originalExpiry && expiryValue && expiryValue >= originalExpiry);
  const expiryCapMessage = `Cannot be later than ${formatExpiryForDisplay(effectiveExpiryMax)} — the expiry of ${expiryCapSource}`;

  /* ── Step navigation ────────────────────────────────────────── */
  const handleNext = async () => {
    const fields = EDIT_STEP_FIELDS[stepper.current] ?? [];
    const ok = fields.length ? await form.trigger(fields) : true;
    if (!ok) return;
    if (stepper.current === 0 && emailStatus === 'taken') {
      form.setError('email', { message: 'Email already exists' });
      return;
    }
    if (stepper.current === 0 && emailReverifyRequired && !emailVerified) {
      toast.error('Re-verify the email address before continuing');
      return;
    }
    if (stepper.current === 0 && !expiryWithinCap) {
      form.setError('expiryDate', { message: expiryCapMessage });
      return;
    }
    if (stepper.current === 1 && canConfigureLeads && form.getValues('role') === USER_ROLES.CALL_CENTRE_ADMIN) {
      const belowAssigned = agentLeadsBelowAssignedMessage(
        form.getValues('agentLeads'),
        Number(target?.agent_leads_associated) || 0,
      );
      if (belowAssigned) {
        form.setError('agentLeads', { message: belowAssigned });
        return;
      }
    }
    stepper.next();
  };

  /* ── Submit ─────────────────────────────────────────────────── */
  const onSubmit = async (input: UpdateUserInput) => {
    if (stepper.current !== STEPS.length - 1) return;
    if (!expiryWithinCap) {
      form.setError('expiryDate', { message: expiryCapMessage });
      stepper.goTo(0);
      return;
    }
    if (emailStatus === 'taken') {
      form.setError('email', { message: 'Email already exists' });
      stepper.goTo(0);
      return;
    }
    if (emailReverifyRequired && !emailVerified) {
      toast.error('Email must be re-verified before submitting');
      stepper.goTo(0);
      return;
    }

    try {
      const payload: Record<string, any> = {
        username: target?.username ?? '',
        name: input.name.trim(),
        email: input.email.trim(),
        status: target?.status ?? 1,
        acl_settings: input.aclSettings || null,
        role: input.role,
      };
      if (emailReverifyRequired && emailVerifiedToken) {
        payload.signup_email_token = emailVerifiedToken;
      }
      if (isCompanyRequired(input.role)) payload.company = input.company || null;
      if (isCallCentreRequired(input.role)) payload.callcenterId = input.callCentreId;
      if (canConfigureLeads && input.role === USER_ROLES.CALL_CENTRE_ADMIN && String(input.agentLeads ?? '').trim() !== '') {
        const agentLeads = parseAgentLeadsInput(input.agentLeads);
        const assigned = Number(target?.agent_leads_associated) || 0;
        if (!agentLeads.ok) {
          form.setError('agentLeads', { message: agentLeads.message });
          stepper.goTo(1);
          return;
        }
        if (agentLeads.value < assigned) {
          form.setError('agentLeads', {
            message: agentLeadsBelowAssignedMessage(input.agentLeads, assigned),
          });
          stepper.goTo(1);
          return;
        }
        payload.agent_leads = agentLeads.value;
      }
      if (canEditExpiry) payload.expiry_date = input.expiryDate;
      const extendChildren = canExtendChildren && extendChildrenExpiry;
      if (extendChildren) {
        payload.extend_children_expiry = true;
        payload.extend_expired_children = extendExpiredChildren;
      }
      if (canEditAccountType) payload.account_type = Number(input.accountType);

      const accountTypeChanged =
        canEditAccountType && String(target?.account_type ?? '0') !== String(input.accountType);

      const res: any = await updateUser.mutateAsync(payload);
      toast.success(
        accountTypeChanged
          ? 'User updated. Account type was applied to every user under this account.'
          : 'User updated',
      );
      if (extendChildren) {
        const extend = res?.expiry_extend;
        if (extend?.failed) {
          toast.error('Could not extend expiry for users under this account. Please try again.');
        } else if (extend) {
          toast.success(`Expiry extended for ${extend.users_updated ?? 0} user(s) and agent(s) under this account.`);
        }
      }
      setTimeout(() => navigate(`/${ROUTES.USERS.LIST}`), 800);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Could not update the user');
    }
  };

  const completedSteps = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < stepper.current; i += 1) arr.push(i);
    return arr;
  }, [stepper.current]);

  if (!canEdit) return <AclDenied operation="edit" />;

  if (profile.isLoading) {
    return (
      <ScrollPage>
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading user…
        </div>
      </ScrollPage>
    );
  }

  if (!target) {
    return (
      <ScrollPage>
        <PageHeader
          title="Edit user"
          description="User not found."
          actions={
            <Button variant="ghost" onClick={() => navigate(`/${ROUTES.USERS.LIST}`)}>
              Back to list
            </Button>
          }
        />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title={`Edit ${target.username}`}
        description="Update this user's details and access."
        actions={
          <Button variant="ghost" onClick={() => navigate(`/${ROUTES.USERS.LIST}`)}>
            Cancel
          </Button>
        }
      />

      <Stepper
        steps={STEPS as unknown as any[]}
        current={stepper.current}
        completed={completedSteps}
        onStepClick={(i) => (i < stepper.current ? stepper.goTo(i) : undefined)}
      />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-6 space-y-6"
          noValidate
        >
          {/* ── Step 0: Account ─────────────────────────────────── */}
          {stepper.current === 0 && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Identity</CardTitle>
                  <CardDescription>
                    Login name, display name, and this user's role.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">Username</label>
                    <Input value={target.username ?? ''} disabled readOnly />
                    <p className="text-xs text-muted-foreground">Username can't be changed.</p>
                  </div>
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full name *</FormLabel>
                        <FormControl>
                          <Input {...field} maxLength={100} placeholder="Full name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Role</label>
                  <Input value={USERS_ROLE[targetRole] || targetRole} disabled readOnly />
                  <p className="text-xs text-muted-foreground">Role can't be changed from this form.</p>
                </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Email</CardTitle>
                  <CardDescription>
                    Contact address; may require re-verification when changed.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <EmailVerifier
                          email={field.value}
                          onEmailChange={(v) => field.onChange(v)}
                          onReset={() => {
                            setEmailVerified(false);
                            setEmailVerifiedToken('');
                          }}
                          onVerified={(token) => {
                            setEmailVerified(true);
                            setEmailVerifiedToken(token);
                          }}
                          required={Boolean(emailReverifyRequired)}
                          verified={emailVerified}
                          emailError={form.formState.errors.email?.message as string | undefined}
                        />
                      </FormControl>
                      {!form.formState.errors.email && (
                        <AvailabilityHint status={emailStatus} takenMessage="Email already exists" />
                      )}
                      {/* EmailVerifier already renders emailError inline;
                          FormMessage would duplicate it. */}
                    </FormItem>
                  )}
                />
                {emailReverifyRequired && (
                  <p className="text-xs text-muted-foreground">
                    Email changed — verification required before saving.
                  </p>
                )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Expiry</CardTitle>
                  <CardDescription>
                    Account expiry date — only the direct parent can change this.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="expiryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expiry date *</FormLabel>
                      <FormControl>
                        <CalendarDateInput
                          value={field.value}
                          onChange={(e) => {
                            const value = e;
                            if (canEditExpiry && effectiveExpiryMax && value && value > effectiveExpiryMax) {
                              toast.error(
                                `Expiry cannot be later than ${formatExpiryForDisplay(effectiveExpiryMax)} — the expiry of ${expiryCapSource}.`,
                              );
                              field.onChange(effectiveExpiryMax);
                              return;
                            }
                            field.onChange(value);
                          }}
                          min={todayStr}
                          max={canEditExpiry && effectiveExpiryMax ? effectiveExpiryMax : undefined}
                          disabled={!canEditExpiry}
                        />
                      </FormControl>
                      {!canEditExpiry ? (
                        <p className="text-xs text-muted-foreground">
                          Only the direct parent can change the expiry date.
                        </p>
                      ) : effectiveExpiryMax ? (
                        <p className="text-xs text-muted-foreground">
                          {`This user cannot expire later than ${formatExpiryForDisplay(effectiveExpiryMax)}, the expiry of ${expiryCapSource}.`}
                        </p>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {canExtendChildren && (
                  <div className="space-y-3 rounded-md border p-3">
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        checked={extendChildrenExpiry}
                        onCheckedChange={(v) => setExtendChildrenExpiry(v === true)}
                        className="mt-0.5"
                      />
                      <span className="space-y-1">
                        <span className="block text-sm font-medium">
                          Also extend expiry for everyone under this user
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          All users and agents created under this account will get the same new
                          expiry date. Anyone who already has a later date, or no expiry date, stays
                          as it is. Leave unticked to change only this user.
                        </span>
                      </span>
                    </label>
                    {extendChildrenExpiry && (
                      <label className="ml-7 flex cursor-pointer items-start gap-3">
                        <Checkbox
                          checked={extendExpiredChildren}
                          onCheckedChange={(v) => setExtendExpiredChildren(v === true)}
                          className="mt-0.5"
                        />
                        <span className="space-y-1">
                          <span className="block text-sm font-medium">
                            Include users whose account has already expired
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            Tick to give expired users and agents the new date too. Leave unticked
                            to extend only users who have not expired yet.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Step 1: Access & Assignment ─────────────────────── */}
          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Access &amp; Assignment</CardTitle>
                <CardDescription>
                  ACL, account type, and organisation this user belongs to.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
              {isAclRequired(role) && (
                <FormField
                  control={form.control}
                  name="aclSettings"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ACL setting *</FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={aclList.isLoading ? 'Loading…' : 'Pick an ACL setting'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(aclList.data ?? []).map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.acl_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="accountType"
                render={({ field }) => {
                  const accountOptions = [
                    {
                      value: '0',
                      label: 'Promotional',
                      hint: 'Marketing / bulk-outreach traffic. DND-honouring.',
                    },
                    {
                      value: '1',
                      label: 'Transactional',
                      hint: 'Alerts, OTPs, service notifications. Delivers to all numbers.',
                    },
                  ];
                  const disabled = !canEditAccountType;
                  const val = field.value ?? '0';
                  return (
                    <FormItem>
                      <FormLabel>Account type *</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={val}
                          onValueChange={field.onChange}
                          disabled={disabled}
                          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                        >
                          {accountOptions.map((opt) => {
                            const selected = val === opt.value;
                            return (
                              <label
                                key={opt.value}
                                htmlFor={`edit-acct-${opt.value}`}
                                className={cn(
                                  'flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 transition-colors',
                                  selected ? 'border-primary ring-1 ring-primary/40' : 'hover:border-ring',
                                  disabled && 'cursor-not-allowed opacity-60',
                                )}
                              >
                                <RadioGroupItem
                                  value={opt.value}
                                  id={`edit-acct-${opt.value}`}
                                  className="mt-0.5"
                                />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium">{opt.label}</div>
                                  <div className="mt-0.5 text-xs text-muted-foreground">
                                    {opt.hint}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {canEditAccountType
                          ? 'Changing this updates every user under this account, including nested users and agents, to the same type.'
                          : 'Account type is set when the user is created. Only a super admin can change it.'}
                      </p>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {isCompanyRequired(role) && (
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company *</FormLabel>
                      <Select
                        value={field.value ?? ''}
                        onValueChange={field.onChange}
                        disabled={isOwnCompany && isCompanyAdmin}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={companyList.isLoading ? 'Loading…' : 'Pick a company'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {filteredCompanies.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {canConfigureLeads && role === USER_ROLES.CALL_CENTRE_ADMIN && (
                <FormField
                  control={form.control}
                  name="agentLeads"
                  render={({ field }) => {
                    const assigned = Number(target?.agent_leads_associated) || 0;
                    const allowance = Number(field.value);
                    const remaining = Number.isFinite(allowance) ? Math.max(0, allowance - assigned) : null;
                    const belowAssigned = agentLeadsBelowAssignedMessage(field.value, assigned);
                    return (
                      <FormItem>
                        <FormLabel>Agent leads *</FormLabel>
                        <FormControl>
                          <Input type="number" min={assigned} step={1} {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          {assigned > 0
                            ? `${assigned.toLocaleString('en-IN')} already assigned, so this cannot go below ${assigned.toLocaleString('en-IN')}.`
                            : 'No leads are assigned yet.'}
                          {remaining != null && !belowAssigned
                            ? ` ${remaining.toLocaleString('en-IN')} would stay free to assign.`
                            : ''}
                        </p>
                        {belowAssigned ? (
                          <p className="text-xs font-medium text-destructive">{belowAssigned}</p>
                        ) : (
                          <FormMessage />
                        )}
                      </FormItem>
                    );
                  }}
                />
              )}

              {isCallCentreRequired(role) && (
                <FormField
                  control={form.control}
                  name="callCentreId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Call centre *</FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={callCentreList.isLoading ? 'Loading…' : 'Pick a call center'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(callCentreList.data ?? []).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.callcentre_name || c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              </CardContent>
            </Card>
          )}

          {/* ── Step 2: Review ──────────────────────────────────── */}
          {stepper.current === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Confirm the changes</CardTitle>
                <CardDescription>
                  Review the updates you made above before saving.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ReviewGrid values={form.getValues()} username={target?.username} showAgentLeads={canConfigureLeads} />
              </CardContent>
            </Card>
          )}

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.current === 0}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            {stepper.current < STEPS.length - 1 ? (
              <Button key="wizard-next" type="button" onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button key="wizard-submit" type="submit" disabled={updateUser.isPending}>
                <Check className="h-4 w-4" />
                {updateUser.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

function ReviewGrid({ values, username, showAgentLeads }: any) {
  const roleLabel = USERS_ROLE[values.role] || values.role || '—';
  const acctLabel = values.accountType === '1' ? 'Transactional' : 'Promotional';
  const rows: Array<[string, any]> = [
    ['Username', username || '—'],
    ['Full name', values.name],
    ['Role', roleLabel],
    ['Email', values.email],
    ['Expiry', values.expiryDate],
    ['Account type', acctLabel],
    ['ACL setting', values.aclSettings || '—'],
    ['Company', values.company || '—'],
    ['Call centre', values.callCentreId || '—'],
    ...(showAgentLeads && values.role === USER_ROLES.CALL_CENTRE_ADMIN
      ? [['Agent leads', values.agentLeads || '—'] as [string, any]]
      : []),
  ];
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map(([label, val]) => (
        <div key={label} className="rounded-md border bg-background/60 px-3 py-2">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </dt>
          <dd className="mt-0.5 truncate text-sm font-medium">{String(val ?? '—')}</dd>
        </div>
      ))}
    </dl>
  );
}
